import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Target, Flame, Loader2, Plus, Sparkles, Compass, Lock, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePaywall } from "@/context/PaywallContext";

const THEME_SUGGESTIONS = ["Healing", "Peace", "Provision", "Faith", "Favor", "Breakthrough", "Identity", "Protection"];
const DAY_PRESETS = [3, 7, 14, 21, 30, 40];

export default function Plans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openPaywall } = usePaywall();
  const free = user?.tier === "free";
  const [plans, setPlans] = useState(null);
  const [mode, setMode] = useState("theme");
  const [theme, setTheme] = useState("");
  const [perDay, setPerDay] = useState(5);
  const [days, setDays] = useState(7);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (free && days > 3) setDays(3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [free]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  const create = async (e) => {
    e?.preventDefault();
    if (mode === "theme" && !theme.trim()) { toast.error("Enter a theme for your plan."); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/plans", {
        mode, theme: mode === "theme" ? theme.trim() : null, per_day: perDay, days,
      });
      toast.success("Your plan is ready — Day 1 awaits!");
      navigate(`/plans/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create plan.");
    } finally {
      setCreating(false);
    }
  };

  const chip = (active) =>
    `px-3.5 py-2 text-sm rounded-full border transition-colors duration-300 ${active ? "bg-[#2C2A29] text-[#FAF9F6] border-[#2C2A29]" : "border-[#D9D4CC] text-[#4A4744] hover:bg-[#EAE6DF]"}`;

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="plans-page">
      <div className="flex items-center gap-3 mb-2">
        <Target className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
        <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">Decree Plans</p>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight">Build a daily habit</h1>
      <p className="font-decree text-lg text-[#7A756E] mt-3">
        Choose a theme or a guided journey, pick your length, and receive a fresh set of decrees each day.
      </p>

      {/* Create form */}
      <form onSubmit={create} className="mt-8 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-6 sm:p-7" data-testid="plan-create-form">
        <p className="font-body text-sm uppercase tracking-[0.15em] text-[#7A756E] mb-4">Start a new plan</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button type="button" data-testid="plan-mode-theme" onClick={() => setMode("theme")}
            className={`flex items-start gap-3 text-left p-4 border rounded-sm transition-colors duration-300 ${mode === "theme" ? "border-[#9B7E4E] bg-[#F3F0EA]" : "border-[#D9D4CC] hover:bg-[#EAE6DF]"}`}>
            <Sparkles className="w-5 h-5 text-[#9B7E4E] shrink-0 mt-0.5" strokeWidth={1.75} />
            <span><span className="block font-heading text-lg text-[#2C2A29]">A single theme</span>
              <span className="block font-body text-sm text-[#7A756E] mt-0.5">Focus on one thing every day</span></span>
          </button>
          <button type="button" data-testid="plan-mode-mix" onClick={() => setMode("mix")}
            className={`flex items-start gap-3 text-left p-4 border rounded-sm transition-colors duration-300 ${mode === "mix" ? "border-[#9B7E4E] bg-[#F3F0EA]" : "border-[#D9D4CC] hover:bg-[#EAE6DF]"}`}>
            <Compass className="w-5 h-5 text-[#9B7E4E] shrink-0 mt-0.5" strokeWidth={1.75} />
            <span><span className="block font-heading text-lg text-[#2C2A29]">Guided journey</span>
              <span className="block font-body text-sm text-[#7A756E] mt-0.5">A new theme each day</span></span>
          </button>
        </div>

        {mode === "theme" && (
          <div className="mt-5" data-testid="plan-theme-field">
            <label className="font-body text-sm text-[#4A4744]">Your theme</label>
            <input
              data-testid="plan-theme-input"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="e.g. healing, peace, favor…"
              className="mt-2 w-full bg-transparent border border-[#D9D4CC] px-4 py-3 text-base text-[#2C2A29] placeholder-[#A39C90] rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none transition-all duration-300"
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {THEME_SUGGESTIONS.map((s) => (
                <button key={s} type="button" onClick={() => setTheme(s)} className={chip(theme === s)}>{s}</button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6">
          <label className="font-body text-sm text-[#4A4744]">Decrees per day</label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="plan-perday">
            {[3, 4, 5, 6, 7].map((n) => (
              <button key={n} type="button" data-testid={`plan-perday-${n}`} onClick={() => setPerDay(n)} className={chip(perDay === n)}>{n}</button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <label className="font-body text-sm text-[#4A4744]">Length: <span className="text-[#2C2A29] font-semibold">{days} days</span></label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="plan-days">
            {DAY_PRESETS.map((n) => {
              const locked = free && n > 3;
              return (
                <button
                  key={n}
                  type="button"
                  data-testid={`plan-days-${n}`}
                  onClick={() => locked
                    ? openPaywall("Free accounts can create 3-day plans. Go Premium for the full 3–40 day range and multiple concurrent plans.")
                    : setDays(n)}
                  className={`${chip(days === n)} ${locked ? "opacity-70" : ""}`}
                >
                  {locked && <Lock className="w-3 h-3 inline mr-1 -mt-0.5" />}{n}
                </button>
              );
            })}
          </div>
          {!free && (
            <input type="range" min="3" max="40" value={days} onChange={(e) => setDays(Number(e.target.value))}
              data-testid="plan-days-slider" className="w-full mt-4 accent-[#9B7E4E]" />
          )}
          {free && (
            <p className="font-body text-xs text-[#9B7E4E] mt-3" data-testid="plan-free-note">
              Free accounts: 3-day plans, one active plan at a time.{" "}
              <button type="button" onClick={() => openPaywall("Go Premium for 3–40 day plans and multiple concurrent plans.")}
                className="underline underline-offset-2 hover:text-[#2C2A29] inline-flex items-center gap-1">
                <Crown className="w-3 h-3" /> Go Premium
              </button>
            </p>
          )}
        </div>

        <button
          data-testid="plan-create-button"
          type="submit"
          disabled={creating}
          className="mt-7 w-full flex items-center justify-center gap-2 px-8 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-50"
        >
          {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing Day 1…</> : <><Plus className="w-4 h-4" /> Create plan</>}
        </button>
      </form>

      {/* Existing plans */}
      <div className="mt-12">
        <h2 className="font-heading text-2xl text-[#2C2A29] tracking-tight mb-5">Your plans</h2>
        {plans === null ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 text-[#2C2A29] animate-spin mx-auto" /></div>
        ) : plans.length === 0 ? (
          <p className="font-decree text-lg text-[#7A756E]" data-testid="plans-empty">No plans yet. Create your first above.</p>
        ) : (
          <ul className="space-y-4" data-testid="plans-list">
            {plans.map((p) => {
              const pct = Math.round((p.completed_count / p.total_days) * 100);
              return (
                <li key={p.id}>
                  <Link to={`/plans/${p.id}`} data-testid={`plan-card-${p.id}`}
                    className="block border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-5 hover:border-[#B7AE9F] transition-colors duration-300">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-heading text-xl text-[#2C2A29]">{p.title}</p>
                        <p className="font-body text-sm text-[#7A756E] mt-0.5">
                          {p.mode === "mix" ? "Guided journey" : "Themed"} · {p.per_day}/day · Day {p.current_day} of {p.total_days}
                        </p>
                      </div>
                      {p.streak > 0 && (
                        <span className="flex items-center gap-1.5 text-[#9B7E4E] font-body text-sm shrink-0">
                          <Flame className="w-4 h-4" strokeWidth={2} /> {p.streak}
                        </span>
                      )}
                    </div>
                    <div className="mt-4 h-1.5 bg-[#EAE6DF] rounded-full overflow-hidden">
                      <div className="h-full bg-[#9B7E4E] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
